import "./App.css";
import AppBarComponent from "./components/Header/AppBarComponent";
import DrawerComponent from "./components/SideBar/DrawerComponent";
import { Routes, Route, useNavigate } from "react-router-dom";
import { LicenseManager } from "ag-grid-enterprise";
import { useAuth0 } from "@auth0/auth0-react";
import { useState, useEffect } from "react";
import DocumentPreview from "./components/Alert/DocumentPreview";
import { GetAntiForgeryToken } from "./api/loginServiceApi/antiForgeryTokenGet";
import { getUsrData } from "./api/loginServiceApi/getUserData";

import "./config/colors.css";
import { useLoader } from "./context/LoaderContext";
import RouteConfig from "./components/Routes/RouteConfig";
import { urlConfig } from "./config/urlConfig";
import { ApplyCurrentTheme } from "./components/NavigationBar/ThemeComponent";
import LocalStorageUtil from "./utils/localStorage";
import SessionManager from "./components/Hooks/SessionManager";
import SessionTimeout from "./components/SessionTimeOutModal/SessionTimeout";

LicenseManager.setLicenseKey(
  "Using_this_{AG_Grid}_Enterprise_key_{AG-057588}_in_excess_of_the_licence_granted_is_not_permitted___Please_report_misuse_to_legal@ag-grid.com___For_help_with_changing_this_key_please_contact_info@ag-grid.com___{Donnelley_Financial_Solutions,_Inc._(USA,_Lisle,_IL,_60532)}_is_granted_a_{Multiple_Applications}_Developer_License_for_{15}_Front-End_JavaScript_developers___All_Front-End_JavaScript_developers_need_to_be_licensed_in_addition_to_the_ones_working_with_{AG_Grid}_Enterprise___This_key_has_been_granted_a_Deployment_License_Add-on_for_{4}_Production_Environments___This_key_works_with_{AG_Grid}_Enterprise_versions_released_before_{22_June_2025}____[v3]_[01]_MTc1MDU0NjgwMDAwMA==7134ea42f6ebe66356830db8670be8a0"
);
const App: React.FC = () => {
  const {
    isLoading,
    isAuthenticated,
    loginWithRedirect,
    logout,
    user,
    getIdTokenClaims,
    getAccessTokenSilently,
    handleRedirectCallback,
  } = useAuth0();
  const [isAntiforgeryCalled, setisAntiforgeryCalled] = useState(false);
  const [businessUnitData, setBusinessUnitData] = useState();
  const { setLoading } = useLoader();
  const navigate = useNavigate();
  const [isDrawerCollapsed, setIsDrawerCollapsed] = useState(true);
  const [userData, setUserData] = useState("");
  const [showSessionTimeoutModal, setShowSessionTimeoutModal] = useState(false);
  const fetchTokenClaims = async () => {
    try {
      // Check if user was previously authenticated
      const wasAuthenticated = localStorage.getItem("wasAuthenticated");      
      if (!isLoading && !isAuthenticated) {
        if (wasAuthenticated === "true") {
          // Session timeout detected
          setShowSessionTimeoutModal(true);
          navigate("/Sessiontimeout");
          // Clear flag after session timeout so it doesn't persist
          localStorage.removeItem("wasAuthenticated");
          return;
        } else {
          // First load, not authenticated
          await loginWithRedirect();
          return;
        }
      } else if (isAuthenticated) {
        // Set flag when user is authenticated
        localStorage.setItem("wasAuthenticated", "true");
        setLoading(true); // Set loading for token retrieval
      }

      const token = await getAccessTokenSilently();
      const tokenClaims = await getIdTokenClaims();
      if (!tokenClaims) {
        return;
      }
      LocalStorageUtil.setItem("token", token);
      if (tokenClaims.email) {
        setUserData(tokenClaims.email);
        getUsrData((tokenClaims.email ?? "default").toString(), navigate);
       
        if (!isAntiforgeryCalled) {
          setisAntiforgeryCalled(true);
          await GetAntiForgeryToken();
        }
        if (LocalStorageUtil.getItem("userId")) {
          setLoading(false);
        }
      }
    } catch (error) {
    } finally {
      setLoading(false); // Ensure loading state is reset
    }
  };

  useEffect(() => {
    const handleAuthRedirect = async () => {
      if (
        window.location.search.includes("code=") &&
        window.location.search.includes("state=")
      ) {
        try {
          // Clear old token before handling redirect
          LocalStorageUtil.removeItem("antiforgery");
          await handleRedirectCallback();
          // Set justLoggedIn flag after redirect
          localStorage.setItem("justLoggedIn", "true");
          window.history.replaceState(
            {},
            document.title,
            window.location.pathname
          ); // Clears query params
        } catch (error) {
          console.error("Error in handleRedirectCallback:", error);
        }
      }
      ApplyCurrentTheme();
    };

    handleAuthRedirect();
    fetchTokenClaims();
    // Use controlled dependencies to avoid unnecessary re-renders
  }, [isAuthenticated, isLoading, handleRedirectCallback]);

  if (isLoading) {
    return <div>Loading...</div>;
  }

  const drawerStyle = {
    flexShrink: 0,
    width: isDrawerCollapsed ? "12%" : "4.33%",
    transition: "width 0.3s ease",
    marginTop: "5em",
  };

  const contentStyle = {
    flexGrow: 1,
    width: !isDrawerCollapsed ? "87%" : "100%",
    transition: "width 0.3s ease",
    paddingTop: "3.8em",
  };

  const getBusinessUnitData = (id: any) => {
    setBusinessUnitData(id);
  };

  const hideDrawerRoutes = "documentviewer";
  const path = !urlConfig.isDeployed
    ? window.location.pathname.split("/")[1]
    : window.location.pathname.split("/")[2];
  const shouldHideDrawer = path === hideDrawerRoutes;
  return (
    <>
      {showSessionTimeoutModal && !isLoading && !isAuthenticated && (
        <Routes>
          <Route
            path="/Sessiontimeout"
            element={
              <SessionTimeout/>
            }
          />
        </Routes>
      )}

      <SessionManager>
        {shouldHideDrawer && (
          <Routes>
            <Route
              path="/documentviewer/:docstreamid"
              element={<DocumentPreview />}
            />
          </Routes>
        )}
        {isAuthenticated && !shouldHideDrawer && (
          <div className="App">
            <AppBarComponent
              logout={() => {
                localStorage.removeItem("wasAuthenticated");
                logout({
                  logoutParams: {
                    returnTo: process.env.REACT_APP_CALLBACK_URL,
                  },
                });
              }}
              user={user}
              getBusinessUnitData={getBusinessUnitData}
              businessUnitData={businessUnitData}
            />
            <div>
              <div style={{ display: "flex", width: "100%" }}>
                <div style={drawerStyle}>
                  <DrawerComponent
                    isCollapsed={isDrawerCollapsed}
                    onToggleCollapse={() =>
                      setIsDrawerCollapsed((prev) => !prev)
                    }
                  />
                </div>
                <div style={contentStyle}>
                  <RouteConfig
                    userData={userData}
                    businessUnitData={businessUnitData}
                  />
                </div>
              </div>
            </div>
          </div>
        )}
      </SessionManager>
    </>
  );
};

export default App;
